import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import TableContainer from '../TableContainer';
import TableHeader from '../TableHeader';
import Pagination10 from '../Pagination10';
import PageFooter from '../PageFooter';
import ItemEditModal from '../modals/ItemEditModal';
import DefaultModal from '../modals/DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';
import LoadingModal from '../modals/LoadingModal';

function DfdFindItemModal({ isOpen, onClose, dfd, dfdLote = null, onAdded }) {
  const token = localStorage.getItem('token');
  const { setDefaultModal, showDefaultModal, closeDefaultModal } = useDefaultModal();

  const normalizeTipo = (tipo) => {
    return (tipo || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  };

  const getCatalogoDefault = (tipo) => {
    const tipoNormalized = normalizeTipo(tipo);
    if (tipoNormalized === 'SERVICO') return 'CATSER';
    if (tipoNormalized === 'MATERIAL') return 'CATMAT';
    return '';
  };

  const [item, setItem] = useState({ descricao: '' });
  const [catalogo, setCatalogo] = useState({ catalogoUtilizado: '' });
  const [opcaoPesquisa, setOpcaoPesquisa] = useState('DESCRICAO');
  const [itens, setItens] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const totalItems = itens.length;
  const [pagination, setPagination] = useState({ startIndex: 0, rowsPerPage: 10 });

  useEffect(() => {
    if (isOpen) {
      // Só define o catálogo padrão se o usuário ainda não tiver escolhido
      if (!catalogo.catalogoUtilizado) {
        const defaultFromTipo = getCatalogoDefault(dfd?.tipo);
        if (defaultFromTipo) {
          setCatalogo({ catalogoUtilizado: defaultFromTipo });
        } else {
          // Se não houver tipo esperado (não é MATERIAL nem SERVICO), usa a primeira opção não-vazia das opções disponíveis
          const firstNonEmpty = (catalogoOptions().find(o => o.value && o.value.trim()) || {}).value || '';
          setCatalogo({ catalogoUtilizado: firstNonEmpty });
        }
      }
    } else {
      closeDefaultModal();
      setShowEditModal(false);
      setSelectedItem(null);
      setItens([]);
      setItem({ descricao: '' });
      setCatalogo({ catalogoUtilizado: '' });
    }
  }, [isOpen, dfd?.tipo, closeDefaultModal, catalogo.catalogoUtilizado]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, startIndex: 0 }));
  }, [itens]);

  const normalizeText = str =>
    (str || '').normalize('NFD').replace(/[-\u036f]/g, '');

  const highlightText = (text, search) => {
    if (!search || !text) return text;
    const normalizedTerm = search.normalize('NFD').replace(/[-\u036f]/g, '');
    const pattern = normalizedTerm.split('').map(c => c).join('');
    const regex = new RegExp(`(${pattern})`, 'gi');
    const highlighted = (text || '').replace(regex, '<mark style="background-color: #ffeb3b; font-weight: bold;">$1</mark>');
    return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
  };
  
  // Versão avançada de destaque que trata acentos e termos (fallback para evitar destaque de todos os caracteres)
  const highlightTextAdvanced = (text, search) => {
    if (!search || !text) return text;

    const normalize = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const searchTerms = search.trim().split(/\s+/);
    let highlightedText = text;

    searchTerms.forEach(term => {
      if (term.length >= 3) {
        const normalizedTerm = normalize(term);
        const pattern = normalizedTerm.split('').map(char => {
          const accentMap = {
            'a': '[aáàâãäå]', 'e': '[eéèêë]', 'i': '[iíìîï]',
            'o': '[oóòôõö]', 'u': '[uúùûü]', 'c': '[cç]', 'n': '[nñ]'
          };
          return accentMap[char.toLowerCase()] || char;
        }).join('');

        try {
          const regex = new RegExp(`(${pattern})`, 'gi');
          highlightedText = highlightedText.replace(regex, '<mark style="background-color: #ffeb3b; font-weight: bold;">$1</mark>');
        } catch (e) {
          console.error('Erro ao criar regex de destaque:', e);
        }
      }
    });

    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  const catalogoOptions = () => {
    const tipoNormalized = normalizeTipo(dfd?.tipo);
    if (tipoNormalized === 'MATERIAL') return [{ value: 'CATMAT', label: 'CATMAT' }, { value: 'E-FISCO/PE', label: 'E-FISCO PE' }];
    if (tipoNormalized === 'SERVICO') return [{ value: 'CATSER', label: 'CATSER' }, { value: 'TABSUS', label: 'TABSUS' }, { value: 'E-FISCO/PE', label: 'E-FISCO PE' }];
    return [{ value: '', label: 'Selecione...' }, { value: 'CATMAT', label: 'CATMAT' }, { value: 'CATSER', label: 'CATSER' }, { value: 'TABSUS', label: 'TABSUS' }, { value: 'E-FISCO/PE', label: 'E-FISCO PE' }];
  };

  const handleChange = e => setItem(prev => ({ ...prev, [e.target.name]: e.target.value.toUpperCase() }));
  const handleChangeCatalogo = e => setCatalogo(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleOpcaoPesquisaChange = e => setOpcaoPesquisa(e.target.value);

  // Quando muda o catálogo ou o tipo de pesquisa, limpa resultados e reseta opções
  useEffect(() => {
    setItens([]);
    setItem(prev => ({ ...prev, descricao: '' }));
  }, [opcaoPesquisa, catalogo.catalogoUtilizado]);

  useEffect(() => {
    setOpcaoPesquisa('DESCRICAO');
  }, [catalogo.catalogoUtilizado]);
  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleFind();
    }
  };

  const handleFind = () => {
    if (!catalogo.catalogoUtilizado) { showDefaultModal('error', 'Selecione o Catálogo Utilizado!'); return; }
    if (!item.descricao) { showDefaultModal('error', 'Preencha a descrição ou código para a pesquisa!'); return; }
    setIsLoading(true);
    let url = '';
    let params = {};
    const cat = catalogo.catalogoUtilizado;
    if (cat === 'CATMAT') {
      if (opcaoPesquisa === 'DESCRICAO') { url = `${API_BASE_URL.CATMAT}/search`; params = { descricao: item.descricao }; }
      else if (opcaoPesquisa === 'CODIGO') { url = `${API_BASE_URL.CATMAT}/search-codigo`; params = { codigo: item.descricao }; }
      else if (opcaoPesquisa === 'PDM') { url = `${API_BASE_URL.CATMAT}/search-pdm`; params = { pdm: item.descricao }; }
      else if (opcaoPesquisa === 'CLASSE') { url = `${API_BASE_URL.CATMAT}/search-classe`; params = { classe: item.descricao }; }
      else if (opcaoPesquisa === 'GRUPO') { url = `${API_BASE_URL.CATMAT}/search-grupo`; params = { grupo: item.descricao }; }
    } else if (cat === 'CATSER') {
      if (opcaoPesquisa === 'DESCRICAO') { url = `${API_BASE_URL.CATSER}/search`; params = { descricao: item.descricao }; }
      else if (opcaoPesquisa === 'CODIGO') { url = `${API_BASE_URL.CATSER}/search-codigo`; params = { codigo: item.descricao }; }
      else if (opcaoPesquisa === 'CLASSE') { url = `${API_BASE_URL.CATSER}/search-classe`; params = { classe: item.descricao }; }
      else if (opcaoPesquisa === 'GRUPO') { url = `${API_BASE_URL.CATSER}/search-grupo`; params = { grupo: item.descricao }; }
    } else if (cat === 'TABSUS') {
      if (opcaoPesquisa === 'DESCRICAO') { url = `${API_BASE_URL.TABSUS}/search`; params = { descricao: item.descricao }; }
      else if (opcaoPesquisa === 'CODIGO') { url = `${API_BASE_URL.TABSUS}/search-codigo`; params = { codigo: item.descricao }; }
      else if (opcaoPesquisa === 'CLASSE') { url = `${API_BASE_URL.TABSUS}/search-classe`; params = { classe: item.descricao }; }
      else if (opcaoPesquisa === 'GRUPO') { url = `${API_BASE_URL.TABSUS}/search-grupo`; params = { grupo: item.descricao }; }
    } else if (cat === 'E-FISCO/PE') {
      if (opcaoPesquisa === 'DESCRICAO') { url = `${API_BASE_URL.EFISCO_PE}/search`; params = { descricao: item.descricao }; }
      else if (opcaoPesquisa === 'CODIGO') { url = `${API_BASE_URL.EFISCO_PE}/search-codigo`; params = { codigo: item.descricao }; }
      else if (opcaoPesquisa === 'CLASSE') { url = `${API_BASE_URL.EFISCO_PE}/search-classe`; params = { classe: item.descricao }; }
      else if (opcaoPesquisa === 'GRUPO') { url = `${API_BASE_URL.EFISCO_PE}/search-grupo`; params = { grupo: item.descricao }; }
    }

    axios.get(url, { params, headers: { Authorization: `Bearer ${token}` } })
      .then(res => setItens(res.data || []))
      .catch(err => { console.error('Erro ao buscar itens:', err); showDefaultModal('error', 'Erro ao buscar itens'); })
      .finally(() => setIsLoading(false));
  };

  const handleOpenEditModal = p => { setSelectedItem(p); setShowEditModal(true); };
  const handleCloseEditModal = () => { setSelectedItem(null); setShowEditModal(false); };

  const handleConfirmAdd = formData => {
    let descricaoFinal = selectedItem.descricao || '';
    if (formData.descricaoComplementar && formData.descricaoComplementar.trim()) descricaoFinal += ', DESCRIÇÃO COMPLEMENTAR: ' + formData.descricaoComplementar;

    const newDfdItem = {
      cnpj: dfd.cnpj,
      dfdNum: dfd.num || dfd.dfdNum,
      tipo: dfd.tipo,
      grupo: selectedItem.grupo,
      classe: selectedItem.classe,
      pdm: selectedItem.pdm,
      item: selectedItem.codigo,
      descricao: descricaoFinal,
      unidade: formData.unidade,
      quantidade: formData.quantidade,
      vlr_unit: '',
      categoria: dfd.tipo === 'MATERIAL' ? 'Material' : 'Serviço',
      catalogoUtilizado: catalogo.catalogoUtilizado,
      tabela: catalogo.catalogoUtilizado + '_' + opcaoPesquisa,
    };

    // Inclui lote se fornecido
    if (dfdLote && (dfdLote.numLote || dfdLote.id)) {
      newDfdItem.loteNum = dfdLote.numLote || dfdLote.id;
    }

    axios.post(`${API_BASE_URL.DFD_ITEM}/saveFind`, newDfdItem, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => {
        showDefaultModal('success', response.data);
        handleCloseEditModal();
        if (onAdded) onAdded();
      })
      .catch(error => {
        const errorMessage = error.response?.data || 'Erro ao salvar o registro';
        console.error('Erro ao salvar:', errorMessage);
        showDefaultModal('error', errorMessage);
      });
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
        <div className="modal-content h-100">
          <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
            <h6 className="modal-title m-0 small"><i className="bi bi-search me-2" />Pesquisa Itens</h6>
            <button
              className="btn btn-link text-white p-0 mb-0"
              onClick={onClose}
              title="Fechar"
              style={{ fontSize: '0.9rem', textDecoration: 'none' }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="modal-body p-2" style={{ flex: 1, overflow: 'hidden' }}>
            <div className="d-flex flex-wrap align-items-center w-100 mb-2" style={{ gap: '0.5rem' }}>
              <div className="d-flex align-items-center flex-shrink-0">
                <label className="form-label me-2 mb-0 small text-muted">Catálogo:</label>
                <select className="form-select form-select-sm" style={{ width: '120px' }} value={catalogo.catalogoUtilizado} onChange={handleChangeCatalogo} name="catalogoUtilizado">
                  {catalogoOptions().map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <label className="form-label me-2 mb-0 small text-muted">Tipo:</label>
                <div className="form-check form-check-inline mb-0">
                  <input className="form-check-input" type="radio" name="opcaoPesquisa" id="findDescricao" value="DESCRICAO" checked={opcaoPesquisa === 'DESCRICAO'} onChange={handleOpcaoPesquisaChange} />
                  <label className="form-check-label small" htmlFor="findDescricao">Descrição</label>
                </div>
                <div className="form-check form-check-inline mb-0">
                  <input className="form-check-input" type="radio" name="opcaoPesquisa" id="findCodigo" value="CODIGO" checked={opcaoPesquisa === 'CODIGO'} onChange={handleOpcaoPesquisaChange} />
                  <label className="form-check-label small" htmlFor="findCodigo">Código</label>
                </div>
                {catalogo.catalogoUtilizado === 'CATMAT' && (
                  <div className="form-check form-check-inline mb-0">
                    <input className="form-check-input" type="radio" name="opcaoPesquisa" id="findPdm" value="PDM" checked={opcaoPesquisa === 'PDM'} onChange={handleOpcaoPesquisaChange} />
                    <label className="form-check-label small" htmlFor="findPdm">PDM</label>
                  </div>
                )}
              </div>

              <div className="input-group input-group-sm search-input-group flex-grow-1" style={{ maxWidth: '320px' }}>
                <span className="input-group-text">
                  <i className="bi bi-search" aria-hidden="true" />
                </span>
                <input onChange={handleChange} onKeyDown={handleKeyDown} value={item.descricao} name="descricao" type="text" className="input-group-text" style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left', fontSize: '0.82rem' }} placeholder="Digite a descrição ou código" />
                <button type="button" onClick={handleFind} className="input-group-text" style={{ cursor: 'pointer' }} title="Pesquisar">
                  <i className="bi bi-send" aria-hidden="true" />
                </button>
                {item.descricao && (
                  <button type="button" className="input-group-text" style={{ cursor: 'pointer' }} title="Limpar" onClick={() => { setItem({ descricao: '' }); setItens([]); }}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>

            <LoadingModal loading={isLoading} />

              <TableContainer
                containerStyle={{ flex: '1 1 auto', minHeight: 0 }}
                responsiveStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
                tableClass="table-custom-radius"
              >
              <TableHeader columns={[
                { label: 'Grupo', width: '8%' },
                { label: 'Classe', width: '8%' },
                ...(catalogo.catalogoUtilizado === 'CATMAT' ? [{ label: 'PDM', width: '8%' }] : []),
                { label: 'Código', width: '10%' },
                { label: 'Descrição' },
                { label: 'Ação', width: '10%' },
              ]} />
              <tbody>
                {(!isLoading && itens.length > 0) ? (
                  itens.slice(pagination.startIndex, pagination.startIndex + pagination.rowsPerPage).map(i => (
                    <tr key={`${i.grupo}-${i.classe}-${i.codigo}`}>
                      <td style={{ width: '8%' }}>{i.grupo}</td>
                      <td style={{ width: '8%' }}>{i.classe}</td>
                      {catalogo.catalogoUtilizado === 'CATMAT' && <td style={{ width: '8%' }}>{i.pdm}</td>}
                      <td style={{ width: '10%' }}>{i.codigo}</td>
                      <td>{opcaoPesquisa === 'DESCRICAO' ? highlightTextAdvanced(i.descricao, item.descricao) : i.descricao}</td>
                      <td className="text-center">
                        <button onClick={() => handleOpenEditModal(i)} className="btn btn-success btn-sm" title="Adicionar item"><i className="bi bi-plus-circle" /></button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6" className="text-center">{isLoading ? 'Carregando...' : 'Nenhum registro encontrado.'}</td></tr>
                )}
              </tbody>
              </TableContainer>
          </div>
          <PageFooter>
              <Pagination10
                totalItems={totalItems}
                rowsPerPage={pagination.rowsPerPage}
                startIndex={pagination.startIndex}
                onRowsPerPageChange={(e) => setPagination({ startIndex: 0, rowsPerPage: Number(e.target.value) })}
                onFirstItems={() => setPagination(prev => ({ ...prev, startIndex: 0 }))}
                onPreviousItems={() => setPagination(prev => ({ ...prev, startIndex: Math.max(0, prev.startIndex - prev.rowsPerPage) }))}
                onNextItems={() => setPagination(prev => ({ ...prev, startIndex: Math.min(totalItems - 1, prev.startIndex + prev.rowsPerPage) }))}
                onLastItems={() => setPagination(prev => ({ ...prev, startIndex: Math.max(0, Math.floor((totalItems - 1) / prev.rowsPerPage) * prev.rowsPerPage) }))}
              />
          </PageFooter>
        </div>
      </div>

      <ItemEditModal show={showEditModal} onClose={handleCloseEditModal} onConfirm={handleConfirmAdd} item={selectedItem} dfdTipo={dfd?.tipo} />
      <DefaultModal {...setDefaultModal} />
    </div>
  );
}

export default DfdFindItemModal;
